import React from "react";
//#region types
import { CompactEditableTableFieldProps, FormFieldProps, GenericFormField } from "models/propTypes/genericFormProps";
import { FormEnums, StyleEnums } from "models/enums";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { FieldArray, FieldArrayRenderProps, useFormikContext } from "formik";
import { Grid, Table, TableBody, TableCell, TableContainer, TableRow } from "@mui/material";
import FormFieldContainerMemoized from "../formFieldContainerMemoized";
import MenuButton from "../../buttons/menuButton";
import RoundButton from "../../buttons/roundButton";
import Icon from "../../icon";
import useCheckVisibility from "../hooks/useCheckVisibility";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

function CompactEditableTableFormField<T extends Record<string, any>>(props: FormFieldProps<T>) {
  const { name, field, value, disabled } = props;

  const [displayRows, setDisplayRows] = React.useState<any[]>([]);
  const [headerRowValues, setHeaderRowValues] = React.useState<any>();
  const [selectedRow, setSelectedRow] = React.useState<number>();
  const [reloadKey, setReloadKey] = React.useState<string>("");
  const previousRowCount = React.useRef<number>(props.value?.length ?? 0);
  const addedRowToFocus = React.useRef<number>();
  const tableRef = React.useRef<HTMLDivElement>(null);

  const { getFieldHidden } = useCheckVisibility();

  const extraProps = field.extraProps as CompactEditableTableFieldProps<any>;

  const formikProps = useFormikContext<T>();
  const { values, dirty } = formikProps;

  React.useEffect(() => {
    setReloadKey(
      field.innerFields
        ? Object.entries(field.innerFields)
            .filter(
              ([key, value]) =>
                value?.type === FormEnums.FormFieldType.COMPACTEDITABLETABLE && !getFieldHidden(value.hidden)
            )
            .map(([key, value]) => key)
            .join("")
        : ""
    );
    if (
      extraProps.headerRowFieldName &&
      JSON.stringify(values[extraProps.headerRowFieldName]) !== JSON.stringify(headerRowValues)
    ) {
      setHeaderRowValues(values[extraProps.headerRowFieldName]);
    }
  }, [values]);

  React.useEffect(() => {
    if (selectedRow === -1) {
      setSelectedRow(undefined);
    } else if (extraProps.activateLastRow && dirty && value.length) {
      setSelectedRow(value.length - 1);
    } else if (extraProps.activateAddedRow && (value?.length ?? 0) > previousRowCount.current) {
      setSelectedRow(value.length - 1);
      addedRowToFocus.current = value.length - 1;
    }
    previousRowCount.current = value?.length ?? 0;

    setDisplayRows(value ?? []);
  }, [value]);

  // The cell inputs mount a few frames after the row itself, so poll for the input instead of focusing in this commit.
  // Not cancelled when displayRows changes again: a follow-up render must not swallow the pending focus.
  React.useEffect(() => {
    const rowIndex = addedRowToFocus.current;
    if (rowIndex === undefined || !extraProps.focusAddedRowField) {
      return;
    }
    addedRowToFocus.current = undefined;
    const selector = `input[name="${name}[${rowIndex}].${extraProps.focusAddedRowField}"]`;
    let framesLeft = 30;
    const focusWhenMounted = () => {
      const input = tableRef.current?.querySelector<HTMLInputElement>(selector);
      if (input) {
        input.focus();
      } else if (--framesLeft > 0) {
        requestAnimationFrame(focusWhenMounted);
      }
    };
    focusWhenMounted();
  }, [displayRows]);

  const onRemoveRow = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    arrayHelpers: FieldArrayRenderProps,
    row: number
  ) => {
    event.stopPropagation();
    setSelectedRow(-1);
    arrayHelpers.remove(row);
    extraProps.onItemRemove && extraProps.onItemRemove(displayRows[row], formikProps);
  };

  const getTableCell = React.useCallback(
    (
      key: number,
      rowIndex: number,
      currentFieldName: string,
      currentField: GenericFormField<any>,
      cellValue: any,
      widthPercentage?: number,
      disabled?: boolean
    ) => {
      const fieldName =
        value !== undefined && rowIndex < value.length ? `${name}[${rowIndex}].${currentFieldName}` : "";
      const errors = props.errors && props.errors[rowIndex] && props.errors[rowIndex][currentFieldName];
      const touched = props.touched && props.touched[rowIndex] && props.touched[rowIndex][currentFieldName];

      const currentFieldExtra = extraProps as CompactEditableTableFieldProps<any>;

      const hidden =
        getFieldHidden(currentField.hidden) ||
        (currentFieldExtra?.cellHideActions &&
          currentFieldExtra.cellHideActions(displayRows[rowIndex], currentFieldName));

      const columnSpan =
        currentFieldExtra?.columnSpanActions &&
        currentFieldExtra.columnSpanActions(displayRows[rowIndex], currentFieldName);

      return !hidden ? (
        <TableCell
          key={`tab-cell-${key}-${currentFieldName}`}
          colSpan={columnSpan}
          className={currentField.classes?.formControl}
          style={{
            width: `${widthPercentage}%`,
            textAlign: currentField.type === FormEnums.FormFieldType.TOGGLE ? "end" : undefined,
          }}
        >
          {
            <FormFieldContainerMemoized
              name={fieldName}
              value={cellValue}
              field={disabled ? { ...currentField, disabled: disabled } : currentField}
              errors={touched && errors}
              touched={touched}
              hideSuccess
              hideErrorBubble
              hideOptionalIndicator
              refValues={displayRows[rowIndex]}
            />
          }
        </TableCell>
      ) : (
        <></>
      );
    },
    [displayRows, props.errors, props.touched, reloadKey]
  );

  const onRowClick = (rowIndex: number) => {
    setSelectedRow(rowIndex);
  };

  const getHeaderRow = () => {
    return (
      <TableRow
        key={`tab-row-header`}
        className={clsx(cssExports.fieldRow, props.disabled ? cssExports.disabled : "", cssExports.headerRow)}
      >
        {field.innerFields &&
          Object.entries(field.innerFields).map(([currentFieldName, currentField]) => {
            const fieldName =
              value !== undefined ? `${extraProps.headerRowFieldName ?? "header"}.${currentFieldName}` : "";
            const widthPercentage =
              currentField?.widthColumnCount && typeof currentField.widthColumnCount === "number"
                ? (currentField.widthColumnCount / 12) * 100
                : undefined;
            return (
              currentField && (
                <TableCell
                  key={`tab-cell-header-${currentFieldName}`}
                  className={currentField.classes?.formControl}
                  style={{
                    width: `${widthPercentage}%`,
                    textAlign: currentField.type === FormEnums.FormFieldType.TOGGLE ? "end" : undefined,
                  }}
                >
                  {
                    <FormFieldContainerMemoized
                      name={fieldName}
                      value={headerRowValues[currentFieldName]}
                      field={{
                        ...currentField,
                        widthColumnCount: undefined,
                        disabled: true,
                        extraProps: { ...currentField.extraProps },
                      }}
                      errors={undefined}
                      touched={undefined}
                      hideSuccess
                      hideErrorBubble
                      hideOptionalIndicator
                    />
                  }
                </TableCell>
              )
            );
          })}
        {!disabled && !extraProps?.hideActions && (
          <TableCell key={`tab-cell-header-last`} className={cssExports.editableTableActionButton}></TableCell>
        )}
      </TableRow>
    );
  };

  const tableRows = React.useCallback(
    (arrayHelpers: FieldArrayRenderProps) => {
      return displayRows.map((row: any, rowIndex: number) => {
        const rowDisabled = extraProps.rowDisabled && extraProps.rowDisabled(row);
        const rowHideActions = extraProps.rowHideActions && extraProps.rowHideActions(row);
        return (
          <TableRow
            key={`tab-row-${rowIndex}`}
            onClick={() => onRowClick(rowIndex)}
            className={clsx(
              cssExports.fieldRow,
              props.disabled || rowDisabled ? cssExports.disabled : "",
              !extraProps.disableRowSelection && selectedRow === rowIndex ? cssExports.rowSelected : ""
            )}
          >
            {field.innerFields &&
              Object.entries(field.innerFields).map(([currentFieldName, currentField]) => {
                return (
                  currentField &&
                  getTableCell(
                    row[extraProps.idFieldKey],
                    rowIndex,
                    currentFieldName,
                    currentField.widthColumnCount ? { ...currentField, widthColumnCount: undefined } : currentField,
                    row[currentFieldName],
                    currentField.widthColumnCount && typeof currentField.widthColumnCount === "number"
                      ? (currentField.widthColumnCount / 12) * 100
                      : undefined
                  )
                );
              })}
            {!disabled && !extraProps?.hideActions && (
              <TableCell
                key={`tab-cell-${row[extraProps.idFieldKey]}-remove`}
                onClick={
                  !extraProps?.moreActions && value !== undefined && rowIndex < value.length && !rowHideActions
                    ? (event: React.MouseEvent<HTMLElement, MouseEvent>) => onRemoveRow(event, arrayHelpers, rowIndex)
                    : undefined
                }
                className={cssExports.editableTableActionButton}
              >
                {value !== undefined &&
                  value.length > rowIndex &&
                  (extraProps?.moreActions && !rowHideActions ? (
                    <MenuButton
                      menuPlacement="bottom"
                      menuItems={
                        extraProps
                          ?.moreActions(row)
                          ?.map((m) =>
                            typeof m === "object"
                              ? { ...m, onClickHandler: m.onClickHandler?.bind(null, rowIndex, row, formikProps) }
                              : m
                          ) ?? []
                      }
                      button={
                        <RoundButton
                          size="xs"
                          displayType={StyleEnums.DisplayType.None}
                          className={cssExports.listMoreActions}
                        >
                          <Icon icon={IconDefinitionConstants.WEllipsisH} />
                        </RoundButton>
                      }
                      classes={{
                        menuOpen: cssExports.listMoreActionsMenuOpen,
                      }}
                    />
                  ) : !rowHideActions ? (
                    <RoundButton size="xs" displayType={StyleEnums.DisplayType.None} className={cssExports.close}>
                      <Icon icon={IconDefinitionConstants.WTimes} />
                    </RoundButton>
                  ) : (
                    <></>
                  ))}
              </TableCell>
            )}
          </TableRow>
        );
      });
    },
    [displayRows, props.errors, props.touched, reloadKey, selectedRow]
  );

  const TableComponent = React.useMemo(
    () => (
      <Grid
        ref={tableRef}
        container
        className={clsx(cssExports.editableTableContainer, cssExports.compactInline)}
        direction="column"
      >
        <TableContainer className={cssExports.tableContainerOuter}>
          <Table aria-label="simple table" className={cssExports.tableInline}>
            <TableBody className={cssExports.tableBody}>
              {extraProps?.externalHeader}
              {headerRowValues && getHeaderRow()}
              <FieldArray name={name} render={(arrayHelpers) => tableRows(arrayHelpers)} />
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    ),
    [displayRows, props.errors, props.touched, selectedRow, headerRowValues]
  );

  return field.innerFields ? TableComponent : <></>;
}

export default CompactEditableTableFormField;
